/**
 * Order Status Display Component
 * Fetches order data from proxy and resolves status based on configurable rules
 */

/**
 * Constants - Centralized string literals
 */
const STATUS_STATES = {
  COMPLETED: "completed",
  ACTIVE: "active",
  PENDING: "pending",
  UNKNOWN: "unknown",
};

const OPERATORS = {
  EQUALS: "equals",
  NOT_EQUALS: "not_equals",
  IN: "in",
  CONTAINS: "contains",
  PATTERN_MATCH: "pattern_match",
};

const FIELD_NAMES = {
  TAGS: "tags",
  FINANCIAL_STATUS: "financialStatus",
  FULFILLMENT_STATUS: "fulfillmentStatus",
  FINANCIAL_STATUS_SNAKE: "financial_status",
  FULFILLMENT_STATUS_SNAKE: "fulfillment_status",
};

const FINANCIAL_STATUSES = {
  PAID: "paid",
  PENDING: "pending",
  AUTHORIZED: "authorized",
  PARTIALLY_PAID: "partially_paid",
  PARTIALLY_REFUNDED: "partially_refunded",
  REFUNDED: "refunded",
  VOIDED: "voided",
};

const FULFILLMENT_STATUSES = {
  FULFILLED: "fulfilled",
  PARTIAL: "partial",
  UNFULFILLED: "unfulfilled",
};

const CONDITION_TYPES = {
  ALL_OF: "all_of",
  ANY_OF: "any_of",
  NONE_OF: "none_of",
};

const ORDER_TYPES = {
  ALL: "all",
  SIMPLE: "simple",
  COMPLEX: "complex",
};

class OrderStatusResolver {
  constructor(rules = [], texts = {}) {
    // Sort rules by priority (higher first)
    this.rules = rules.sort((a, b) => (b.priority || 0) - (a.priority || 0));
    this.texts = texts;
  }

  /**
   * Resolve order status based on order data and rules
   * @param {Object} orderData - Raw order data from Shopify
   * @returns {Object} Resolved status with display info
   */
  resolve(orderData) {
    for (const rule of this.rules) {
      // Skip rules without explicit order_type
      if (!rule.order_type) continue;

      if (this.matchesRule(orderData, rule)) {
        return {
          key: rule.internal_key,
          label: rule.display_name,
          description: rule.description,
          icon_html: rule.icon_html,
          color: rule.color,
          priority: rule.priority,
          order_type: rule.order_type,
          extractedData: this.extractData(
            orderData,
            rule.matching_rules?.extract_data || {}
          ),
        };
      }
    }

    // Fallback default status
    return this.getDefaultStatus(orderData, this.texts);
  }

  /**
   * Check if order matches a specific rule
   */
  matchesRule(order, rule) {
    if (!rule.matching_rules) return false;

    const conditions = rule.matching_rules.conditions || rule.matching_rules;

    // Check all_of conditions (AND)
    if (conditions[CONDITION_TYPES.ALL_OF]) {
      const allMatch = conditions[CONDITION_TYPES.ALL_OF].every((condition) =>
        this.evaluateCondition(order, condition)
      );
      if (!allMatch) return false;
    }

    // Check any_of conditions (OR)
    if (conditions[CONDITION_TYPES.ANY_OF]) {
      const anyMatch = conditions[CONDITION_TYPES.ANY_OF].some((condition) =>
        this.evaluateCondition(order, condition)
      );
      if (!anyMatch) return false;
    }

    // Check none_of conditions (NOT)
    if (conditions[CONDITION_TYPES.NONE_OF]) {
      const noneMatch = conditions[CONDITION_TYPES.NONE_OF].every(
        (condition) => !this.evaluateCondition(order, condition)
      );
      if (!noneMatch) return false;
    }

    return true;
  }

  /**
   * Evaluate a single condition
   */
  evaluateCondition(order, condition) {
    const { field, operator, value, values, pattern } = condition;
    let fieldValue = this.getFieldValue(order, field);

    // Normalize status fields to lowercase for comparison
    if (
      field === FIELD_NAMES.FINANCIAL_STATUS_SNAKE ||
      field === FIELD_NAMES.FINANCIAL_STATUS ||
      field === FIELD_NAMES.FULFILLMENT_STATUS_SNAKE ||
      field === FIELD_NAMES.FULFILLMENT_STATUS
    ) {
      fieldValue = fieldValue ? String(fieldValue).toLowerCase() : fieldValue;
    }

    let result;
    switch (operator) {
      case OPERATORS.EQUALS:
        result = fieldValue === value;
        break;

      case OPERATORS.NOT_EQUALS:
        result = fieldValue !== value;
        break;

      case OPERATORS.IN:
        result = values && values.includes(fieldValue);
        break;

      case OPERATORS.CONTAINS:
        // Field must exist and not be null/undefined
        if (!fieldValue && fieldValue !== 0 && fieldValue !== false) {
          result = false;
          break;
        }

        if (Array.isArray(fieldValue)) {
          result = fieldValue.some((item) =>
            String(item).toLowerCase().includes(String(value).toLowerCase())
          );
        } else {
          result = String(fieldValue)
            .toLowerCase()
            .includes(String(value).toLowerCase());
        }
        break;

      case OPERATORS.PATTERN_MATCH:
        // Field must exist and not be null/undefined
        if (!fieldValue && fieldValue !== 0 && fieldValue !== false) {
          result = false;
          break;
        }

        // Use RegEx pattern matching (case-insensitive by default)
        try {
          const regex = new RegExp(pattern, "i");
          if (Array.isArray(fieldValue)) {
            result = fieldValue.some((item) => regex.test(String(item)));
          } else {
            result = regex.test(String(fieldValue));
          }
        } catch (e) {
          console.warn(`Invalid regex pattern: ${pattern}`, e);
          result = false;
        }
        break;

      default:
        console.warn(`Unknown operator: ${operator}`);
        result = false;
    }

    return result;
  }

  /**
   * Extract additional data from order using regex patterns
   */
  extractData(order, extractConfig) {
    const extracted = {};

    for (const [key, config] of Object.entries(extractConfig)) {
      if (config.from_tag_pattern) {
        const regex = new RegExp(config.from_tag_pattern, "i");
        const tags = this.getFieldValue(order, FIELD_NAMES.TAGS) || [];

        for (const tag of tags) {
          const match = String(tag).match(regex);
          if (match) {
            if (config.capture_groups) {
              extracted[key] = config.capture_groups.map((idx) => match[idx]);
            } else {
              const captureIdx = config.capture_group || 1;
              extracted[key] = match[captureIdx];
            }
            break;
          }
        }
      }

      if (config.computed) {
        extracted[key] = this.computeValue(extracted, config.computed);
      }
    }

    return extracted;
  }

  /**
   * Compute derived values (e.g., date + X days)
   */
  computeValue(extractedData, expression) {
    const match = expression.match(/(\w+)\s*\+\s*(\d+)\s*days?/);
    if (match) {
      const [, sourceKey, daysToAdd] = match;
      const sourceDate = this.parseDate(extractedData[sourceKey]);
      if (sourceDate) {
        const result = new Date(sourceDate);
        result.setDate(result.getDate() + parseInt(daysToAdd, 10));
        return result.toLocaleDateString("de-DE");
      }
    }
    return null;
  }

  /**
   * Parse date from various formats
   */
  parseDate(dateStr) {
    if (!dateStr) return null;
    const match = String(dateStr).match(/(\d{2})[\.\-](\d{2})[\.\-](\d{4})/);
    if (match) {
      const [, day, month, year] = match;
      return new Date(year, month - 1, day);
    }
    return null;
  }

  /**
   * Get nested field value from object
   */
  getFieldValue(obj, fieldPath) {
    return fieldPath.split(".").reduce((current, key) => current?.[key], obj);
  }

  /**
   * Default status when no rules match
   */
  getDefaultStatus(orderData, texts) {
    return {
      key: STATUS_STATES.UNKNOWN,
      label: texts.statusDetermining,
      description: texts.statusDeterminingDescription,
      icon_html:
        '<svg fill="none" stroke="currentColor" viewBox="0 0 24 24" width="40" height="40"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>',
      color: "#6B7280",
      priority: 0,
      order_type: ORDER_TYPES.ALL,
      extractedData: {},
    };
  }
}

class OrderStatusDisplay extends HTMLElement {
  constructor() {
    super();
    this.initConfig();
    this.initTexts();
    this.initLabels();
    this.initStatusTranslations();
    this.initOrderParams();
    this.initResolver();
    this.start();
  }

  /**
   * Initialize configuration from data attributes
   */
  initConfig() {
    this.proxyUrl = this.dataset.proxyUrl;
    this.xApiKey = this.dataset.xApiKey;
    this.moneyFormat = this.dataset.moneyFormat || "{{amount}} €";
  }

  /**
   * Initialize text labels from data attributes
   */
  initTexts() {
    this.texts = {
      statusDetermining: this.dataset.textStatusDetermining || "Status pending",
      statusDeterminingDescription:
        this.dataset.textStatusDeterminingDescription ||
        "We are checking the current status of your order.",
      orderNotFound:
        this.dataset.textOrderNotFound ||
        "Order not found. Please check your details.",
      errorLoading:
        this.dataset.textErrorLoading ||
        "An error occurred while loading the order. Please try again later.",
      noOrderNumber:
        this.dataset.textNoOrderNumber || "No order number provided",
      noOrderNumberDescription:
        this.dataset.textNoOrderNumberDescription ||
        "Please use the form to search for your order.",
    };
  }

  /**
   * Initialize UI labels from data attributes
   */
  initLabels() {
    this.labels = {
      orderDetails: this.dataset.labelOrderDetails || "Order Details",
      orderNumber: this.dataset.labelOrderNumber || "Order Number",
      orderDate: this.dataset.labelOrderDate || "Order Date",
      paymentStatus: this.dataset.labelPaymentStatus || "Payment Status",
      status: this.dataset.labelStatus || "Status",
      estimatedDelivery:
        this.dataset.labelEstimatedDelivery || "Estimated Delivery",
      shippingAddress: this.dataset.labelShippingAddress || "Shipping Address",
    };
  }

  /**
   * Initialize status translations from data attributes
   */
  initStatusTranslations() {
    this.statusTranslations = {
      payment: {
        paid: this.dataset.statusPaid || "Paid",
        notPaid: this.dataset.statusNotPaid || "Pending",
      },
      financial: {
        paid: this.dataset.statusPaid || "Paid",
        pending: this.dataset.statusNotPaid || "Pending",
        authorized: this.dataset.financialStatusAuthorized || "Authorized",
        partially_paid:
          this.dataset.financialStatusPartiallyPaid || "Partially paid",
        partially_refunded:
          this.dataset.financialStatusPartiallyRefunded || "Partially refunded",
        refunded: this.dataset.financialStatusRefunded || "Refunded",
        voided: this.dataset.financialStatusVoided || "Voided",
      },
      fulfillment: {
        fulfilled: this.dataset.fulfillmentStatusFulfilled || "Fulfilled",
        partial: this.dataset.fulfillmentStatusPartial || "Partially fulfilled",
        unfulfilled: this.dataset.fulfillmentStatusUnfulfilled || "Unfulfilled",
      },
    };
  }

  /**
   * Initialize order parameters from URL or data attributes
   */
  initOrderParams() {
    const { orderNumber, email } = this.parseUrlParams();
    this.orderNumber = this.dataset.orderNumber || orderNumber;
    this.email = this.dataset.email || email;
  }

  /**
   * Parse order parameters from URL (handles # in order numbers)
   */
  parseUrlParams() {
    const urlParams = new URLSearchParams(window.location.search);
    let orderNumber = urlParams.get("order");
    let email = urlParams.get("email");

    // Handle non-encoded # in URL (browser treats it as fragment)
    if (window.location.hash) {
      const hash = window.location.hash.substring(1);
      const hashParams = new URLSearchParams(hash);

      // If order ends with "-", append the first part of hash as order number
      if (orderNumber && orderNumber.endsWith("-") && /^\d+/.test(hash)) {
        const orderNumberFromHash = hash.match(/^(\d+)/)[1];
        orderNumber = orderNumber + "#" + orderNumberFromHash;
      }

      // Extract email from hash if not in query
      if (!email && hashParams.has("email")) {
        email = hashParams.get("email");
      }
    }

    return { orderNumber, email };
  }

  /**
   * Initialize the status resolver
   */
  initResolver() {
    this.resolver = new OrderStatusResolver(
      window.ORDER_STATUS_RULES || [],
      this.texts
    );
  }

  /**
   * Start the component (auto-fetch or show error)
   */
  start() {
    if (this.orderNumber) {
      this.fetchOrderStatus();
    } else {
      this.showNoOrderNumberError();
    }
  }

  async fetchOrderStatus() {
    try {
      const body = {
        orderNumber: this.orderNumber.trim(),
      };

      if (this.email) {
        body.email = this.email.trim().toLowerCase();
      }

      const response = await fetch(this.proxyUrl, {
        method: "POST",
        body: JSON.stringify(body),
        headers: {
          "Content-Type": "application/json",
          "X-API-KEY": this.xApiKey,
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      console.log("📦 Order Status API Response:", data);

      // Check if order was found
      // Response structure can be { order: {...} } OR direct order data
      const orderData = data.order || data;
      console.log("📋 Order Data (extracted):", orderData);

      // Check if we have valid order data (orderId or orderNumber must exist)
      if (
        !orderData ||
        (!orderData.orderId && !orderData.orderNumber) ||
        data.error
      ) {
        this.showError(data.message || this.texts.orderNotFound);
        return;
      }

      // Resolve status using rules
      const status = this.resolver.resolve(orderData);

      // Render status display
      this.renderStatus(status, orderData);
    } catch (error) {
      console.error("Error fetching order status:", error);
      this.showError(this.texts.errorLoading);
    }
  }

  /**
   * Convert Rich Text JSON to HTML string
   */
  richTextToHtml(richText) {
    if (!richText) return "";
    if (typeof richText === "string") return richText;
    if (typeof richText !== "object") return String(richText);

    // Rich Text structure: { type: "root", children: [...] }
    if (richText.type === "root" && richText.children) {
      return richText.children.map((node) => this.convertNode(node)).join("");
    }

    return "";
  }

  convertNode(node) {
    if (!node) return "";

    if (node.type === "text") {
      let text = node.value || "";
      if (node.bold) text = `<strong>${text}</strong>`;
      if (node.italic) text = `<em>${text}</em>`;
      return text;
    }

    if (node.type === "paragraph") {
      const content = node.children
        ? node.children.map((child) => this.convertNode(child)).join("")
        : "";
      return `<p>${content}</p>`;
    }

    if (node.type === "heading" && node.level) {
      const content = node.children
        ? node.children.map((child) => this.convertNode(child)).join("")
        : "";
      return `<h${node.level}>${content}</h${node.level}>`;
    }

    if (node.type === "list") {
      const tag = node.listType === "ordered" ? "ol" : "ul";
      const content = node.children
        ? node.children.map((child) => this.convertNode(child)).join("")
        : "";
      return `<${tag}>${content}</${tag}>`;
    }

    if (node.type === "list-item") {
      const content = node.children
        ? node.children.map((child) => this.convertNode(child)).join("")
        : "";
      return `<li>${content}</li>`;
    }

    return "";
  }

  /**
   * Replace placeholders in text with dynamic values
   */
  replacePlaceholders(text, order, extractedData = {}) {
    if (!text) return "";

    // Convert Rich Text JSON to HTML if needed
    let result = this.richTextToHtml(text);

    // 1. [payment_status] → Use status translations
    const paymentStatus = order.financialStatus || order.financial_status;
    const paymentStatusText =
      paymentStatus &&
      String(paymentStatus).toLowerCase() === FINANCIAL_STATUSES.PAID
        ? this.statusTranslations.payment.paid
        : this.statusTranslations.payment.notPaid;

    result = result.replace(/\[payment_status\]/gi, paymentStatusText);

    // 2. [order_date] → Order date from order data
    const orderDate = order.createdAt || order.created_at;
    if (orderDate) {
      const formattedOrderDate = this.formatDate(orderDate);
      result = result.replace(/\[order_date\]/gi, formattedOrderDate);
    }

    // 3. [production_date] → Date from extractedData
    if (extractedData.production_date) {
      const formattedDate =
        this.formatDateToEuropean(extractedData.production_date) ||
        extractedData.production_date;
      result = result.replace(/\[production_date\]/gi, formattedDate);
    }

    // 4. [warehouse_arrival] → Warehouse Arrival Date
    if (extractedData.warehouse_arrival) {
      const formattedDate =
        this.formatDateToEuropean(extractedData.warehouse_arrival) ||
        extractedData.warehouse_arrival;
      result = result.replace(/\[warehouse_arrival\]/gi, formattedDate);
    }

    // 5. [delivery_date] → Date Range
    let deliveryDateText = "";
    let earliestDate, latestDate;

    if (
      extractedData.delivery_range &&
      Array.isArray(extractedData.delivery_range)
    ) {
      [earliestDate, latestDate] = extractedData.delivery_range;
    } else {
      earliestDate =
        extractedData.warehouse_arrival || extractedData.delivery_date;
      latestDate =
        extractedData.confirmed_delivery || extractedData.estimated_delivery;
    }

    earliestDate = this.formatDateToEuropean(earliestDate);
    latestDate = this.formatDateToEuropean(latestDate);

    if (earliestDate && latestDate && earliestDate !== latestDate) {
      deliveryDateText = `${earliestDate} - ${latestDate}`;
    } else {
      deliveryDateText = earliestDate || latestDate || "";
    }

    if (deliveryDateText) {
      result = result.replace(/\[delivery_date\]/gi, deliveryDateText);
    }

    return result;
  }

  renderStatus(status, order) {
    // Hide loading state
    const loadingState = this.querySelector(".loading-state");
    if (loadingState) loadingState.hidden = true;

    // Show result container
    const resultContainer = this.querySelector(".order-status-result");
    resultContainer.hidden = false;

    // Build status display
    resultContainer.innerHTML = `
      <div class="order-details">
        <div class="order-details-columns">
          ${this.renderOrderDetailsColumn(order, status)}
          ${this.email ? this.renderShippingAddress(order) : ""}
        </div>
      </div>
      ${
        status.key !== STATUS_STATES.UNKNOWN
          ? this.renderTimeline(status, order)
          : ""
      }
      ${
        order.updatedAt
          ? `<div class="last-updated">Zuletzt aktualisiert ${this.getRelativeTime(
              order.updatedAt
            )}</div>`
          : ""
      }
    `;
  }

  /**
   * Render a single detail row
   */
  renderDetailRow(label, value) {
    return `
      <div class="detail-row">
        <span class="detail-row__label">${label}</span>
        <span class="detail-row__value">${value}</span>
      </div>
    `;
  }

  /**
   * Render order details column
   */
  renderOrderDetailsColumn(order, status) {
    const orderNumber = order.name || order.orderNumber || this.orderNumber;
    const orderDate =
      order.createdAt || order.created_at
        ? this.formatDate(order.createdAt || order.created_at)
        : "-";
    const paymentStatus =
      order.financialStatus || order.financial_status
        ? this.translateFinancialStatus(
            order.financialStatus || order.financial_status
          )
        : "-";

    return `
      <div class="order-details-column">
        <h2 class="h5">${this.labels.orderDetails}</h2>
        ${this.renderDetailRow(this.labels.orderNumber, orderNumber)}
        ${this.renderDetailRow(this.labels.orderDate, orderDate)}
        ${this.renderDetailRow(this.labels.paymentStatus, paymentStatus)}
        ${
          status.key !== STATUS_STATES.UNKNOWN
            ? this.renderDetailRow(this.labels.status, status.label)
            : ""
        }
        ${this.renderEstimatedDelivery(status.extractedData)}
      </div>
    `;
  }

  renderTimeline(status, order) {
    const allRules = window.ORDER_STATUS_RULES || [];
    if (allRules.length === 0) return "";

    // Filter rules by order_type from matched status
    const activeOrderType = status.order_type || ORDER_TYPES.ALL;
    const filteredRules = allRules.filter((rule) => {
      // Skip rules without order_type
      if (!rule.order_type) return false;

      return (
        rule.order_type === ORDER_TYPES.ALL ||
        rule.order_type === activeOrderType
      );
    });

    if (filteredRules.length === 0) return "";

    // Group rules by timeline step (floor(sort_order / 10))
    const stepGroups = {};
    filteredRules.forEach((rule) => {
      const step = Math.floor(rule.priority / 10);
      if (!stepGroups[step]) {
        stepGroups[step] = [];
      }
      stepGroups[step].push(rule);
    });

    // Get sorted unique steps
    const steps = Object.keys(stepGroups)
      .map(Number)
      .sort((a, b) => a - b);

    // Find current step from matched status
    const currentStep = Math.floor(status.priority / 10);
    const lastStep = steps[steps.length - 1];

    // Build status boxes
    return `
      <div class="status-steps">
        ${steps
          .map((step) =>
            this.renderTimelineStep(
              step,
              currentStep,
              lastStep,
              steps.length,
              stepGroups,
              status,
              order
            )
          )
          .join("")}
      </div>
    `;
  }

  /**
   * Render a single timeline step
   */
  renderTimelineStep(
    step,
    currentStep,
    lastStep,
    totalSteps,
    stepGroups,
    status,
    order
  ) {
    const isCompleted = step < currentStep;
    const isActive = step === currentStep;
    const isLastStepAndActive = isActive && step === lastStep && totalSteps > 1;
    const showCheckBadge = isCompleted || isLastStepAndActive;

    // Determine step class
    const stepClass =
      isCompleted || isLastStepAndActive
        ? STATUS_STATES.COMPLETED
        : isActive
        ? STATUS_STATES.ACTIVE
        : STATUS_STATES.PENDING;

    // Get the rule to display
    const stepRules = stepGroups[step];
    const highestPriorityRule = stepRules.sort(
      (a, b) => b.priority - a.priority
    )[0];
    const displayRule = isActive && status ? status : highestPriorityRule;

    // Get description based on step state
    const descriptionWithPlaceholders = this.getStepDescription(
      displayRule,
      isCompleted,
      isLastStepAndActive,
      isActive
    );
    const description = this.replacePlaceholders(
      descriptionWithPlaceholders,
      order,
      status.extractedData || {}
    );

    return this.renderTimelineStepBox(
      displayRule,
      stepClass,
      showCheckBadge,
      description,
      isActive && !isLastStepAndActive
    );
  }

  /**
   * Get description text based on step state
   */
  getStepDescription(displayRule, isCompleted, isLastStepAndActive, isActive) {
    if (isCompleted || isLastStepAndActive) {
      return displayRule.description_completed || displayRule.description || "";
    } else if (isActive) {
      return displayRule.description || "";
    } else {
      return displayRule.description_pending || displayRule.description || "";
    }
  }

  /**
   * Render timeline step box HTML
   */
  renderTimelineStepBox(
    displayRule,
    stepClass,
    showCheckBadge,
    description,
    isActive = false
  ) {
    const checkBadgeSvg = `<div class="status-step-box__check-badge"><svg fill="currentColor" viewBox="0 0 20 20" width="12" height="12"><path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd"/></svg></div>`;
    const liveBadge = `<div class="status-step-box__live-badge"><span class="live-dot"></span><span class="live-text">LIVE</span></div>`;

    return `
      <div class="status-step-box ${stepClass}">
        ${isActive ? liveBadge : ""}
        <div class="status-step-box__icon">
          ${displayRule.icon_html || ""}
          ${showCheckBadge ? checkBadgeSvg : ""}
        </div>
        <div class="status-step-box__content">
          <h3 class="h6">${displayRule.label || displayRule.display_name}</h3>
          <div class="status-step-box__description">${description}</div>
        </div>
      </div>
    `;
  }

  renderEstimatedDelivery(extractedData) {
    if (!extractedData) return "";

    let earliestDate, latestDate;

    // Check for delivery_range (array from capture_groups)
    if (
      extractedData.delivery_range &&
      Array.isArray(extractedData.delivery_range)
    ) {
      [earliestDate, latestDate] = extractedData.delivery_range;
    } else {
      // Fallback to individual fields
      earliestDate =
        extractedData.warehouse_arrival || extractedData.delivery_date;
      latestDate =
        extractedData.confirmed_delivery || extractedData.estimated_delivery;
    }

    // Format dates to DD.MM.YYYY
    earliestDate = this.formatDateToEuropean(earliestDate);
    latestDate = this.formatDateToEuropean(latestDate);

    // If we have both dates (range), show as range
    if (earliestDate && latestDate && earliestDate !== latestDate) {
      return `
        <div class="detail-row">
          <span class="detail-row__label">${this.labels.estimatedDelivery}</span>
          <span class="detail-row__value">${earliestDate} - ${latestDate}</span>
        </div>
      `;
    }

    // If we only have one date, show that
    const singleDate = earliestDate || latestDate;
    if (!singleDate) return "";

    return `
      <div class="detail-row">
        <span class="detail-row__label">${this.labels.estimatedDelivery}</span>
        <span class="detail-row__value">${singleDate}</span>
      </div>
    `;
  }

  formatDateToEuropean(dateStr) {
    if (!dateStr) return null;

    // Match YYYY-MM-DD or YYYY/MM/DD format (with 1 or 2 digit days/months)
    const isoMatch = dateStr.match(/^(\d{4})[-/](\d{1,2})[-/](\d{1,2})$/);
    if (isoMatch) {
      const [, year, month, day] = isoMatch;
      return `${day.padStart(2, "0")}.${month.padStart(2, "0")}.${year}`;
    }

    // Match DD-MM-YYYY or DD/MM/YYYY format (with 1 or 2 digit days/months)
    const europeanMatch = dateStr.match(/^(\d{1,2})[-/](\d{1,2})[-/](\d{4})$/);
    if (europeanMatch) {
      const [, day, month, year] = europeanMatch;
      return `${day.padStart(2, "0")}.${month.padStart(2, "0")}.${year}`;
    }

    // Match DD.MM.YYYY format (with 1 or 2 digit days/months)
    const dotMatch = dateStr.match(/^(\d{1,2})\.(\d{1,2})\.(\d{4})$/);
    if (dotMatch) {
      const [, day, month, year] = dotMatch;
      return `${day.padStart(2, "0")}.${month.padStart(2, "0")}.${year}`;
    }

    // Fallback: return as-is
    return dateStr;
  }

  renderShippingAddress(order) {
    const address = order.shippingAddress || order.shipping_address;
    if (!address) return "";

    return `
      <div class="order-details-column">
        <h2 class="h5">${this.labels.shippingAddress}</h2>
        <div class="shipping-address">
          ${
            address.name || (address.firstName && address.lastName)
              ? `<p class="address-name">${
                  address.name || `${address.firstName} ${address.lastName}`
                }</p>`
              : ""
          }
          ${
            address.address1 || address.street
              ? `<p>${address.address1 || address.street}</p>`
              : ""
          }
          ${address.address2 ? `<p>${address.address2}</p>` : ""}
          ${
            address.zip || address.city
              ? `<p>${address.zip || ""} ${address.city || ""}</p>`
              : ""
          }
          ${
            address.country || address.countryCode
              ? `<p>${address.country || address.countryCode}</p>`
              : ""
          }
        </div>
      </div>
    `;
  }

  showError(message) {
    const loadingState = this.querySelector(".loading-state");
    if (loadingState) loadingState.hidden = true;

    const errorState = this.querySelector(".error-state");
    errorState.hidden = false;
    errorState.querySelector(".error-message").textContent = message;
  }

  showNoOrderNumberError() {
    const loadingState = this.querySelector(".loading-state");
    if (loadingState) loadingState.hidden = true;

    const errorState = this.querySelector(".error-state");
    errorState.hidden = false;

    const heading = errorState.querySelector("h3");
    heading.textContent = this.texts.noOrderNumber;

    const message = errorState.querySelector(".error-message");
    message.textContent = this.texts.noOrderNumberDescription;
  }

  formatDate(dateString) {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return date.toLocaleDateString("de-DE", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    });
  }

  getRelativeTime(dateString) {
    if (!dateString) return "";

    const now = new Date();
    const date = new Date(dateString);
    const diffMs = now - date;
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffHours < 24) {
      // Less than 24 hours: show hours
      const hoursText = diffHours === 1 ? "Stunde" : "Stunden";
      return `vor ${diffHours} ${hoursText}`;
    } else {
      // More than 24 hours: show days
      const daysText = diffDays === 1 ? "Tag" : "Tagen";
      return `vor ${diffDays} ${daysText}`;
    }
  }

  translateFinancialStatus(status) {
    if (!status) return "-";
    const statusLower = String(status).toLowerCase();
    return this.statusTranslations.financial[statusLower] || status;
  }

  translateFulfillmentStatus(status) {
    if (!status || status === null)
      return this.statusTranslations.fulfillment.unfulfilled;
    const statusLower = String(status).toLowerCase();
    return this.statusTranslations.fulfillment[statusLower] || status;
  }
}

if (!window.customElements.get("order-status-display")) {
  window.customElements.define("order-status-display", OrderStatusDisplay);
}
